// useGithubRepo.ts
import { supabase, Database } from './supabase';
import { getGithubAccountByUserId, isGithubTokenExpired, refreshGithubAccountTokens, checkAndUpdateGithubToken } from './useGithubAccount';

export type GithubRepo = Database['public']['Tables']['github_repo']['Row'];
export type GithubSyncStatus = 'pending' | 'syncing' | 'synced' | 'error';

/**
 * Creates a new entry linking a game to a GitHub repository for a user.
 * First, checks if the GitHub repository exists in the user's GitHub account.
 * If it doesn't exist, it creates the GitHub repository on GitHub.
 * After creating, it initializes the main branch with a README.md file.
 * Finally, it stores the repository link in the database.
 * @param userId The UUID of the authenticated user.
 * @param gameId The UUID of the game being linked.
 * @param repoName The name of the GitHub repository (e.g., 'my-game-repo').
 * @param status The initial status of the repository sync.
 * @returns The created GithubRepo record.
 * @throws Error if the operation fails, including GitHub API errors or missing access token.
 */
export async function createGithubRepo(
  userId: string,
  gameId: string,
  repoName: string,
  status: string
): Promise<GithubRepo> {
  try {
    // Check if a github_repo row already exists for this user/game
    try {
      const existing = await getGithubRepo(userId, gameId);
      if (existing) {
        return existing;
      }
    } catch {}

    // 1. Fetch GitHub Account to get the access token and username, ensuring token is valid
    const githubAccount = await checkAndUpdateGithubToken(userId);

    const accessToken = githubAccount.github_access_token;
    const githubUsername = githubAccount.github_username;

    // 2. Check if the GitHub repository exists
    const checkRepoUrl = `https://api.github.com/repos/${githubUsername}/${repoName}`;
    let repoExists = false;
    try {
      const response = await fetch(checkRepoUrl, {
        headers: {
          'Authorization': `token ${accessToken}`,
          'Accept': 'application/vnd.github.v3+json'
        }
      });

      if (response.status === 200) {
        repoExists = true;
        // console.log(`GitHub repository '${githubUsername}/${repoName}' already exists.`);
      } else if (response.status === 404) {
        // console.log(`GitHub repository '${githubUsername}/${repoName}' does not exist. Attempting to create.`);
        repoExists = false;
      } else {
        const errorData = await response.json();
        console.error('Error checking GitHub repository existence:', errorData);
        throw new Error(`Failed to check GitHub repository existence: ${response.status} - ${errorData.message || 'Unknown error'}`);
      }
    } catch (fetchError: any) {
      console.error('Network error while checking GitHub repository:', fetchError);
      throw new Error(`Network error checking GitHub repository: ${fetchError.message}`);
    }

    // 3. If the repository doesn't exist, create it
    if (!repoExists) {
      const createRepoUrl = `https://api.github.com/user/repos`; // Create for authenticated user
      try {
        const createResponse = await fetch(createRepoUrl, {
          method: 'POST',
          headers: {
            'Authorization': `token ${accessToken}`,
            'Accept': 'application/vnd.github.v3+json',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            name: repoName, // Use the provided repoName directly
            private: true // Default to private as per user's request
          })
        });

        if (createResponse.status === 201) {
          // console.log(`GitHub repository '${githubUsername}/${repoName}' created successfully.`);

          // Initialize main branch with README.md
          const readmeContent = "playmix.ai game";
          const base64Content = btoa(readmeContent); // Base64 encode the content
          const createReadmeUrl = `https://api.github.com/repos/${githubUsername}/${repoName}/contents/README.md`;

          try {
            const readmeResponse = await fetch(createReadmeUrl, {
              method: 'PUT',
              headers: {
                'Authorization': `token ${accessToken}`,
                'Accept': 'application/vnd.github.v3+json',
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                message: "Initial commit: Add README.md",
                content: base64Content,
                branch: "main" // Ensure it's pushed to the main branch
              })
            });

            if (readmeResponse.status === 201) {
              // console.log(`README.md created successfully in '${githubUsername}/${repoName}'.`);
            } else {
              const errorData = await readmeResponse.json();
              console.error('Error creating README.md:', errorData);
              throw new Error(`Failed to create README.md: ${readmeResponse.status} - ${errorData.message || 'Unknown error'}`);
            }
          } catch (readmeError: any) {
            console.error('Network error while creating README.md:', readmeError);
            throw new Error(`Network error creating README.md: ${readmeError.message}`);
          }

        } else {
          const errorData = await createResponse.json();
          console.error('Error creating GitHub repository:', errorData);
          throw new Error(`Failed to create GitHub repository: ${createResponse.status} - ${errorData.message || 'Unknown error'}`);
        }
      } catch (createError: any) {
        console.error('Network error while creating GitHub repository:', createError);
        throw new Error(`Network error creating GitHub repository: ${createError.message}`);
      }
    }

    // 4. Store the repository information in the database (insert new row)
    const { data, error } = await supabase
      .from('github_repo')
      .insert({
        user_id: userId,
        game_id: gameId,
        repo_name: repoName,
        status: status,
        // 'created_at' and 'updated_at' handled by database defaults/triggers
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating GitHub repo entry in database:', error);
      throw new Error(`Failed to create GitHub repo entry in database: ${error.message}`);
    }
    if (!data) {
      throw new Error('No data returned after creating GitHub repo entry in database.');
    }
    return data as GithubRepo;
  } catch (err: any) {
    console.error('Exception in createGithubRepo:', err);
    throw err;
  }
}

/**
 * Retrieves a single GitHub repository detail linked to a specific game and user.
 * Both gameId and userId are required to pinpoint a unique repository.
 * @param userId The UUID of the user.
 * @param gameId The UUID of the game.
 * @returns The GithubRepo record or null if not found.
 * @throws Error if the operation fails.
 */
export async function getGithubRepo(userId: string, gameId: string): Promise<GithubRepo | null> {
    // Check for no user ID or no game ID
    if (!userId) {
      throw new Error('User ID is required.');
    }
    if (!gameId) {
      throw new Error('Game ID is required.');
    }
  
    try {
      const { data, error } = await supabase
        .from('github_repo')
        .select('*')
        .eq('user_id', userId)
        .eq('game_id', gameId)
        .maybeSingle();
  
      if (error) {
        console.error('Error fetching GitHub repo:', error);
      }
  
      return (data || null) as GithubRepo | null;
    } catch (err) {
      console.error('Exception in getGithubRepo:', err);
      throw err;
    }
}

/**
 * Updates the status of a linked GitHub repository.
 * @param userId The UUID of the user who owns the repository.
 * @param gameId The UUID of the game associated with the repository.
 * @param newStatus The new status string (e.g., 'syncing', 'synced', 'error').
 * @returns The updated GithubRepo record.
 * @throws Error if the operation fails or the record is not found.
 */
export async function updateGithubRepoStatus(userId: string, gameId: string, newStatus: string): Promise<GithubRepo> {
  try {
    const { data, error } = await supabase
      .from('github_repo')
      .update({
        status: newStatus,
        // 'updated_at' will be handled by a database trigger.
      })
      .eq('user_id', userId)
      .eq('game_id', gameId)
      .select()
      .single();

    if (error) {
      console.error('Error updating GitHub repo status:', error);
      throw new Error(`Failed to update GitHub repo status: ${error.message}`);
    }
    if (!data) {
      throw new Error('No data returned after updating GitHub repo status. Record might not exist.');
    }
    return data as GithubRepo;
  } catch (err) {
    console.error('Exception in updateGithubRepoStatus:', err);
    throw err;
  }
}

/**
 * Retrieves the current sync status for a specific game's GitHub repository.
 * @param userId The UUID of the user.
 * @param gameId The UUID of the game.
 * @param pollSeconds The number of seconds to poll for the sync status. If 0 (default), it doesn't poll at all.
 * @returns GithubSyncStatus string.
 * @throws Error if the operation fails.
 */
export async function checkGithubSyncStatus(userId: string, gameId: string, pollSeconds: number = 0): Promise<GithubSyncStatus> {
  let rawStatus: GithubSyncStatus = 'pending';
  let isSyncing = false;

  try {
    const githubRepo = await getGithubRepo(userId, gameId);
    
    // Check if a GitHub repo exists. If not, it should be considered "syncing".
    if (!githubRepo) {

      // console.log('No GitHub repository found. Syncing is considered true.');
      rawStatus = null;
      isSyncing = true;

    } else { // If there is a github repo

      if (pollSeconds > 0) {
        const maxAttempts = pollSeconds;
        const delay = 1000; // 1 second
        
        for (let i = 0; i < maxAttempts; i++) {
          // console.log(`Status check attempt ${i + 1}`);
  
          // Fetch the status inside the loop for polling
          const currentGithubRepo = await getGithubRepo(userId, gameId);
          rawStatus = currentGithubRepo?.status as GithubSyncStatus;
  
          // Check if it's no longer syncing (i.e., 'synced' or 'error')
          const isNotSyncing = rawStatus !== 'syncing';
          if (isNotSyncing) {
            // Exit early if the status is anything other than 'syncing'
            isSyncing = rawStatus !== 'synced';
            break; // Exit the loop
          }
          // Wait for the next attempt
          if (i < maxAttempts - 1) {
            await new Promise(resolve => setTimeout(resolve, delay));
          }
          isSyncing = true;
        }
  
      } else {
        rawStatus = githubRepo.status as GithubSyncStatus;
        // console.log('Raw status from getGithubRepo (no polling):', githubRepo?.status);
        isSyncing = rawStatus !== 'synced';
      }
      
    }

  } catch (err) {
    console.error('Exception in checkGithubSyncStatus:', err);
    throw err;
  }

  // console.log(`Github sync status: ${rawStatus}`);
  return rawStatus;
}

/**
 * Updates the repository name for a linked GitHub repository.
 * @param gameId The UUID of the game associated with the repository.
 * @param newRepoName The new repository name (e.g., 'owner/new-repo-name').
 * @returns The updated GithubRepo record.
 * @throws Error if the operation fails or the record is not found.
 */
export async function updateGithubRepoName(gameId: string, newRepoName: string): Promise<GithubRepo> {
  try {
    const { data, error } = await supabase
      .from('github_repo')
      .update({
        repo_name: newRepoName,
        // 'updated_at' will be handled by a database trigger.
      })
      .eq('game_id', gameId)
      .select()
      .single();

    if (error) {
      console.error('Error updating GitHub repo name:', error);
      throw new Error(`Failed to update GitHub repo name: ${error.message}`);
    }
    if (!data) {
      throw new Error('No data returned after updating GitHub repo name. Record might not exist.');
    }
    return data as GithubRepo;
  } catch (err) {
    console.error('Exception in updateGithubRepoName:', err);
    throw err;
  }
}

/**
 * Deletes a GitHub repository connection to a specific game.
 * @param gameId The UUID of the game whose linked repository entry to delete.
 * @returns True if deletion was successful, false otherwise.
 * @throws Error if the operation fails.
 */
export async function deleteGithubRepoConnection(gameId: string): Promise<boolean> {
  try {
    const { error, count } = await supabase
      .from('github_repo')
      .delete()
      .eq('game_id', gameId)
      .select(); // Use select() to get count of deleted rows

    if (error) {
      console.error('Error deleting GitHub repo entry:', error);
      throw new Error(`Failed to delete GitHub repo entry: ${error.message}`);
    }
    // Supabase delete() returns count of deleted rows if select() is used.
    // If no rows were found to delete, count will be 0.
    return (count || 0) > 0;
  } catch (err) {
    console.error('Exception in deleteGithubRepoConnection:', err);
    throw err;
  }
}
